import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CHECKED_ICON, UNCHECKED_ICON } from '../common/InvoiceTableCommon';
import CloseIconButton from '../../../../components/common/CloseIconButton';
import {
  useUnsettledDonorInvoices,
  bulkAssignDonorInvoices,
} from './newInvoiceForm.helper';
import type { UnsettledDonorInvoice } from './newInvoiceForm.helper';
import { useAlert } from '../../../../components/alertProvider';
import { usePatientContext } from '../../../../components/patinetProvider';
import { TABLE_HEADER_ROW_SX } from '../../../../utils/typographyStyles';
import { formatDateDMY } from '../../../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const TITLE_ID = 'unsettled-donor-bill-dialog-title';
const DONOR_SURROGATE_TYPES = ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'];
const DISMISSED_KEY_PREFIX = 'unsettled_donor_dismissed_';

/** SessionStorage key per patient — prevents the popup from re-opening after dismissal. */
const getDismissedKey = (patientId: number | string) =>
  `${DISMISSED_KEY_PREFIX}${patientId}`;
const isDismissedForPatient = (patientId: number | string): boolean => {
  try { return sessionStorage.getItem(getDismissedKey(patientId)) === '1'; }
  catch { return false; }
};
const markDismissedForPatient = (patientId: number | string): void => {
  try { sessionStorage.setItem(getDismissedKey(patientId), '1'); }
  catch { /* quota errors are non-critical */ }
};

// ---------------------------------------------------------------------------
// Styles — co-located, single source of truth 
// ---------------------------------------------------------------------------
const DIALOG_PAPER_SX = { borderRadius: '16px', overflow: 'hidden' };

const CELL_SX = { verticalAlign: 'middle' as const, px: 2.5, py: 1.5 };

const WARNING_ICON_SX = {
  width: 48,
  height: 48,
  minWidth: 48,
  borderRadius: '50%',
  backgroundColor: '#FFF3E0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mt: 0.25,
};

const TABLE_CONTAINER_SX = {
  mt: 2,
  border: '1px solid #EEEEEE',
  borderRadius: '10px',
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 400,
};

const TABLE_SX = {
  minWidth: 850,
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
  '& th, & td': { borderBottom: '1px solid', borderColor: 'divider' },
};

const ADD_BTN_SX = {
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none' as const,
  minWidth: 200,
  px: 3.5,
  py: 1,
  fontSize: '0.875rem',
  backgroundColor: '#212121',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#333', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' },
};

const CANCEL_BTN_SX = {
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none' as const,
  minWidth: 100,
  px: 3,
  py: 1,
  fontSize: '0.875rem',
  borderColor: '#E0E0E0',
  color: '#424242',
  '&:hover': { borderColor: '#BDBDBD', backgroundColor: '#FAFAFA' },
  '&.Mui-disabled': { borderColor: '#EEEEEE', color: '#BDBDBD' },
};

const POPOVER_PAPER_SX = {
  p: 2,
  borderRadius: 2,
  minWidth: 280,
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a number/string amount with currency symbol */
const fmtAmt = (val: string | number | undefined | null, symbol: string): string => {
  if (val == null) return `${symbol}0.00`;
  const n = typeof val === 'number' ? val : parseFloat(val);
  return `${symbol}${Number.isNaN(n) ? '0.00' : n.toFixed(2)}`;
};

/** Parse a string|number to a number safely */
const parseAmt = (val: string | number | undefined | null): number => {
  if (val == null) return 0;
  const n = typeof val === 'number' ? val : parseFloat(val);
  return Number.isNaN(n) ? 0 : n;
};

/** Group selected invoices by donor_patient_id for the bulk assign API payload */
const buildDonorInvoicesPayload = (selected: UnsettledDonorInvoice[]) => {
  const grouped = selected.reduce<Record<number, number[]>>((acc, inv) => {
    const did = inv.donor_patient_id;
    if (!acc[did]) acc[did] = [];
    acc[did].push(inv.id);
    return acc;
  }, {});
  return Object.entries(grouped).map(([donorId, invoiceIds]) => ({
    donor_patient_id: Number(donorId),
    invoice_ids: invoiceIds,
  }));
};

/** Build popover breakdown rows for a given invoice */
const getAmountBreakdown = (inv: UnsettledDonorInvoice, symbol: string) => [
  { label: `Total Amt. (${symbol})`, value: fmtAmt(inv.total_amount, symbol), bold: true },
  { label: `Concession Amt. (${symbol})`, value: fmtAmt(inv.concession_amount, symbol), bold: true },
  { label: `Paid Amt. (${symbol})`, value: fmtAmt(inv.paid_amount, symbol), bold: false },
  { label: `Balance Amt. (${symbol})`, value: fmtAmt(inv.balance_amount, symbol), bold: false },
];

/** Mask a name: show first letter of each word, mask the rest. e.g. "Grace Turner" → "G**** T*****" */
const maskName = (name: string): string => {
  if (!name) return '-';
  return name
    .split(' ')
    .map((word) => (word.length > 0 ? word[0] + '*'.repeat(Math.max(word.length - 1, 0)) : ''))
    .join(' ');
};

/** Row hover/selection background */
const getSelectedRowSx = (isSelected: boolean) => ({
  height: 60,
  cursor: 'pointer',
  backgroundColor: isSelected ? '#FFF8F0' : 'transparent',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: isSelected ? '#FFEFD5 !important' : 'rgba(0,0,0,0.02)',
  },
});

// ---------------------------------------------------------------------------
// Table header column config
// ---------------------------------------------------------------------------
const getHeaderColumns = (currencySymbol: string) => [
  { key: 'donor', label: 'Donor', align: 'left' as const },
  { key: 'date', label: 'Date', align: 'left' as const },
  { key: 'bill_type', label: 'Bill Type', align: 'left' as const },
  { key: 'provider', label: 'Provider', align: 'left' as const },
  { key: 'total_amt', label: `Total Amt. (${currencySymbol})`, align: 'right' as const },
  { key: 'net_amount', label: `Net Amount (${currencySymbol})`, align: 'right' as const },
  { key: 'balance', label: `Balance (${currencySymbol})`, align: 'right' as const },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface UnsettledDonorBillDetectorProps {
  /** Currency symbol for display, e.g. "$", "₹". */
  currencySymbol?: string;
  /** Called with the invoices the user selected to add. */
  onAddToRecipient?: (selected: UnsettledDonorInvoice[]) => void;
  /** Called with the invoices the user selected to cancel/dismiss. */
  onCancelInvoices?: (selected: UnsettledDonorInvoice[]) => void;
}

interface DonorBillDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (selected: UnsettledDonorInvoice[]) => void;
  onCancel: (selected: UnsettledDonorInvoice[]) => void;
  invoices: UnsettledDonorInvoice[];
  loading: boolean;
  /** True while the bulk-assign mutation is in flight */
  submitting?: boolean;
  donorName?: string;
  donorMrn?: string;
  currencySymbol: string;
}

// ---------------------------------------------------------------------------
// DonorBillDialog — presentational
// ---------------------------------------------------------------------------
const DonorBillDialog: React.FC<DonorBillDialogProps> = ({
  open,
  onClose,
  onAdd,
  onCancel,
  invoices,
  loading,
  submitting = false,
  donorName,
  donorMrn,
  currencySymbol,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverInvoice, setPopoverInvoice] = useState<UnsettledDonorInvoice | null>(null);

  // Track which donor identities have been revealed
  const [revealedDonorIds, setRevealedDonorIds] = useState<Set<number>>(new Set());
  const toggleRevealDonor = useCallback((donorPatientId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setRevealedDonorIds((prev) => {
      const next = new Set(prev);
      if (next.has(donorPatientId)) next.delete(donorPatientId); else next.add(donorPatientId);
      return next;
    });
  }, []);

  // Reset selection when dialog opens/closes so stale checkboxes don't persist
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setRevealedDonorIds(new Set());
    }
  }, [open]);

  const handleInfoClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>, inv: UnsettledDonorInvoice) => {
      e.stopPropagation();
      setPopoverAnchor(e.currentTarget as unknown as HTMLElement);
      setPopoverInvoice(inv);
    },
    [],
  );

  const handleInfoClose = useCallback(() => {
    setPopoverAnchor(null);
    setPopoverInvoice(null);
  }, []);

  const allSelected = invoices.length > 0 && selectedIds.size === invoices.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const noneSelected = selectedIds.size === 0;

  const handleToggle = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === invoices.length ? new Set() : new Set(invoices.map((inv) => inv.id)),
    );
  }, [invoices]);

  // Memoize selected invoices — used by both action buttons
  const selectedInvoices = useMemo(
    () => invoices.filter((inv) => selectedIds.has(inv.id)),
    [invoices, selectedIds],
  );

  const selectedTotal = useMemo(
    () => selectedInvoices.reduce((sum, inv) => sum + parseAmt(inv.net_amount), 0),
    [selectedInvoices],
  );

  const donorLabel = donorName
    ? `${maskName(donorName)}${donorMrn ? ` | ${donorMrn}` : ''}`
    : 'A linked donor';

  // Check if invoices come from multiple donors
  const uniqueDonorIds = useMemo(() => {
    const ids = new Set(invoices.map((inv) => inv.donor_patient_id));
    return ids.size;
  }, [invoices]);
  const isMultipleDonors = uniqueDonorIds > 1;

  const headerColumns = useMemo(() => getHeaderColumns(currencySymbol), [currencySymbol]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={TITLE_ID}
      slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}
    >
      <CloseIconButton
        onClick={onClose}
        aria-label="Close unsettled donor bill dialog"
        data-testid="donor-bill-close-btn"
        sx={{ position: 'absolute', right: 16, top: 16 }}
      />

      {/* ---- Header ---- */}
      <DialogTitle id={TITLE_ID} sx={{ pb: 0, pt: 3.5, px: 3.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={WARNING_ICON_SX}>
            <ErrorOutlineIcon sx={{ color: '#E65100', fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.3, color: '#212121' }}>
              Unsettled Donor Bill Detected
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', mt: 0.75, lineHeight: 1.55, fontSize: '0.84rem' }}>
              {isMultipleDonors ? (
                <>The linked donors have unsettled invoices. You may select the outstanding bills below to include them in the recipient&apos;s invoice before proceeding with settlement.</>
              ) : (
                <>Donor <strong style={{ color: '#212121' }}>&ldquo;{donorLabel}&rdquo;</strong> has
                unsettled invoices. You may select the outstanding bills below to include them in the
                recipient&apos;s invoice before proceeding with settlement.</>
              )}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      {/* ---- Content ---- */}
      <DialogContent sx={{ px: 3.5, pt: 2.5, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: '#E65100' }} />
          </Box>
        ) : invoices.length === 0 ? (
          <Typography variant="body2" sx={{ py: 3, color: 'text.secondary', textAlign: 'center' }}>
            No unsettled donor invoices found.
          </Typography>
        ) : (
          <TableContainer sx={TABLE_CONTAINER_SX}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_SX}>
                  <TableCell padding="checkbox" sx={{ pl: 2.5, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleToggleAll}
                      size="small"
                      icon={UNCHECKED_ICON}
                      checkedIcon={CHECKED_ICON}
                      disableRipple
                      sx={{ p: 0 }}
                      data-testid="donor-bill-select-all"
                    />
                  </TableCell>
                  {headerColumns.map((col, idx) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{
                        whiteSpace: 'nowrap',
                        px: 2.5,
                        ...(idx === headerColumns.length - 1
                          ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 }
                          : {}),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {invoices.map((inv) => {
                  const isSelected = selectedIds.has(inv.id);
                  return (
                    <TableRow
                      key={inv.id}
                      hover
                      onClick={() => handleToggle(inv.id)}
                      sx={getSelectedRowSx(isSelected)}
                      data-testid={`donor-bill-row-${inv.id}`}
                    >
                      <TableCell padding="checkbox" sx={{ ...CELL_SX, pl: 2.5 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggle(inv.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                          disableRipple
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem', color: '#212121', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {revealedDonorIds.has(inv.donor_patient_id)
                                ? (inv.donor_name || '-')
                                : maskName(inv.donor_name || '')}
                            </Typography>
                            {inv.donor_mrn && (
                              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.72rem' }}>
                                {inv.donor_mrn}
                              </Typography>
                            )}
                          </Box>
                          {inv.donor_name && (
                            <Box
                              component="span"
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) => toggleRevealDonor(inv.donor_patient_id, e)}
                              sx={{ cursor: 'pointer', display: 'inline-flex', flexShrink: 0, color: '#9e9e9e', '&:hover': { color: '#616161' } }}
                              title={revealedDonorIds.has(inv.donor_patient_id) ? 'Hide donor name' : 'Reveal donor name'}
                            >
                              {revealedDonorIds.has(inv.donor_patient_id)
                                ? <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                : <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                        {inv.visit_date ? formatDateDMY(inv.visit_date) : '-'}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        {inv.bill_type || '-'}
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                        {inv.provider_name_label || '-'}
                      </TableCell>
                      <TableCell align="right" sx={CELL_SX}>
                        {fmtAmt(inv.total_amount, currencySymbol)}
                      </TableCell>
                      <TableCell align="right" sx={CELL_SX}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          {fmtAmt(inv.net_amount, currencySymbol)}
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, color: '#5A8AEA', cursor: 'pointer' }}
                            onClick={(e) => handleInfoClick(e, inv)}
                            data-testid={`donor-bill-info-${inv.id}`}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={CELL_SX}>
                        {fmtAmt(inv.balance_amount, currencySymbol)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* ---- Amount Details Popover ---- */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleInfoClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: POPOVER_PAPER_SX } }}
      >
        {popoverInvoice && (
          <Box data-testid="donor-bill-amount-popover">
            {getAmountBreakdown(popoverInvoice, currencySymbol).map((row) => (
              <Box
                key={row.label}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6 }}
              >
                <Typography variant="body2" sx={{ color: '#616161', mr: 2, whiteSpace: 'nowrap' }}>
                  {row.label}&nbsp;&nbsp;:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: row.bold ? 700 : 600, color: '#212121', whiteSpace: 'nowrap' }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>

      {/* ---- Selection Summary ---- */}
      {!loading && invoices.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              px: 3.5,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: noneSelected ? 'transparent' : '#FFF8F0',
            }}
          >
            <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.8125rem' }}>
              {noneSelected
                ? `${invoices.length} unsettled invoice(s)`
                : `${selectedIds.size} of ${invoices.length} invoice(s) selected`}
            </Typography>
            {!noneSelected && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#E65100', fontSize: '0.875rem' }}>
                Total: {fmtAmt(selectedTotal, currencySymbol)}
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* ---- Actions ---- */}
      <DialogActions sx={{ px: 3.5, pb: 3, pt: 2, justifyContent: 'flex-end', gap: 1.5 }}>
        <Button
          variant="outlined"
          disabled={noneSelected || submitting}
          onClick={() => onCancel(selectedInvoices)}
          sx={CANCEL_BTN_SX}
          data-testid="donor-bill-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={noneSelected || submitting}
          onClick={() => onAdd(selectedInvoices)}
          sx={ADD_BTN_SX}
          data-testid="donor-bill-add-btn"
        >
          {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Add to Recipient Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// UnsettledDonorBillDetector — owns API, state, auto-open, and dialog
// ---------------------------------------------------------------------------
const UnsettledDonorBillDetector: React.FC<UnsettledDonorBillDetectorProps> = ({
  currencySymbol = '$',
  onAddToRecipient,
  onCancelInvoices,
}) => {
  const { addAlert } = useAlert();
  const { MainpatientDetails: currentPatient } = usePatientContext();
  const queryClient = useQueryClient();

  const isDonorOrSurrogate = DONOR_SURROGATE_TYPES.includes(currentPatient?.patient_type ?? '');

  const donorIds = useMemo(() => {
    if (isDonorOrSurrogate) return [];
    const details = currentPatient?.donor_details;
    if (!Array.isArray(details) || details.length === 0) return [];
    return details.map((d: { id: number }) => d.id).filter(Boolean);
  }, [currentPatient?.donor_details, isDonorOrSurrogate]);

  const patientId = currentPatient?.id;
  const { data, isLoading } = useUnsettledDonorInvoices(donorIds);

  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);

  // Auto-open once per session — skip if user already dismissed for this patient
  useEffect(() => {
    if (
      !shownRef.current &&
      patientId &&
      !isDismissedForPatient(patientId) &&
      data?.unsettled_invoices?.length
    ) {
      shownRef.current = true;
      setOpen(true);
    }
  }, [data, patientId]);

  // Bulk assign mutation (TanStack Query)
  const bulkAssignMutation = useMutation({
    mutationFn: (selected: UnsettledDonorInvoice[]) =>
      bulkAssignDonorInvoices({
        recipient_patient_id: currentPatient!.id,
        donor_invoices: buildDonorInvoicesPayload(selected),
      }),
    onSuccess: async (_data, selected) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      addAlert(`${selected.length} donor invoice(s) added to recipient invoice.`, 'success');
      setOpen(false);
    },
    onError: () => {
      addAlert('Failed to assign donor invoices. Please try again.', 'error');
    },
  });

  /** Shared dismiss: mark dismissed + close dialog */
  const handleDismiss = useCallback(() => {
    if (patientId) markDismissedForPatient(patientId);
    setOpen(false);
  }, [patientId]);

  const handleAdd = useCallback(
    (selected: UnsettledDonorInvoice[]) => {
      handleDismiss();
      if (onAddToRecipient) {
        onAddToRecipient(selected);
        return;
      }
      bulkAssignMutation.mutate(selected);
    },
    [onAddToRecipient, bulkAssignMutation, handleDismiss],
  );

  const handleCancel = useCallback(
    (selected: UnsettledDonorInvoice[]) => {
      handleDismiss();
      if (onCancelInvoices) {
        onCancelInvoices(selected);
      } else {
        addAlert(
          `${selected.length} donor invoice(s) dismissed for this recipient (no changes were made to the invoices).`,
          'info',
        );
      }
    },
    [onCancelInvoices, addAlert, handleDismiss],
  );

  // Derive donor name / MRN for display
  const donorName = useMemo(() => {
    if (data?.unsettled_invoices?.[0]?.donor_name) return data.unsettled_invoices[0].donor_name;
    const d = currentPatient?.donor_details?.[0];
    return d ? `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() || undefined : undefined;
  }, [data, currentPatient?.donor_details]);

  const donorMrn =
    data?.unsettled_invoices?.[0]?.donor_mrn
    ?? currentPatient?.donor_details?.[0]?.medical_record_no
    ?? undefined;

  if (donorIds.length === 0) return null;

  return (
    <DonorBillDialog
      open={open}
      onClose={handleDismiss}
      onAdd={handleAdd}
      onCancel={handleCancel}
      invoices={data?.unsettled_invoices ?? []}
      loading={isLoading}
      submitting={bulkAssignMutation.isPending}
      donorName={donorName}
      donorMrn={donorMrn}
      currencySymbol={currencySymbol}
    />
  );
};

export default UnsettledDonorBillDetector;
export type { UnsettledDonorInvoice };
